import { FC, MouseEvent, useEffect, useState } from 'react'
// import LazyLoad from 'react-lazyload'

import { RenderIf } from '../../RenderIf'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { AllergenImages } from '../../../featureSliced/shared/utils/AllergenImages'
import { convertToPrice } from '../../../featureSliced/shared/utils/convertToPrice'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { IOrderProduct } from '../../../sections/Order/types'

import fullHeart from 'assets/hearts/full_s.svg'
import emptyHeart from 'assets/hearts/empty_s.svg'
import { ReactComponent as NoteIcon } from 'assets/notes/small_note.svg'
import { ReactComponent as ArrowRight } from 'assets/arrowRight.svg'
import { ReactComponent as Squares } from 'assets/squares.svg'
import { ReactComponent as CameraIcon } from 'assets/cameraIcon.svg'

import {
  AgeProtection,
  Allergen,
  AllergensContainer,
  FlatwareImageContainer,
  ImagePlaceholder,
  IncludedItems,
  KitchenItem,
  KitchenItemContainer,
  Name,
  Note,
  Price,
  PriceOG,
  ProductStyled,
  SecondaryImage,
  StyledImage,
  SubTemplateContainer,
} from './styled'
import { useOpenNote } from 'featureSliced/shared/hooks/openModals/useOpenNote'
import {
  formatLeadDaysNotice,
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'
import { handleLocalStorage } from '../../../featureSliced/shared/utils/handleLocalStorage'
import { IconFilled, IconStroked } from '../../IconsContainers/styled'
import { useTranslations } from '../../../featureSliced/shared/hooks/useTranslations'
import { ProductImageContainer, SoldOutBanner } from '../ProductGrided/styled'
import { getProtectedAge } from '../../../featureSliced/shared/utils/getProtectedAge'
import { isProductProtected } from '../../../featureSliced/shared/utils/isProductProtected'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { WishlistSlice } from 'featureSliced/shared/store/reducers/WishlistReducer'
import { ImageContainer } from 'featureSliced/widgets/Cart/CartProduct/styled'

type ProductProps = {
  product: IProduct | IOrderProduct
  templateId: ProductTemplateIdType
  onProductClick: (product: IProduct, e: MouseEvent<HTMLElement>) => void
  showPricePerItem?: boolean

  isSelected?: boolean
  amount?: number
  altCount?: number
  includedItems?: any[]
  selectedKitchenInfo?: string[]
}

export const ProductListedComponent: FC<ProductProps> = ({
  product,
  templateId,
  onProductClick,
  showPricePerItem = false,

  isSelected,
  amount,
  altCount,
  includedItems,
  selectedKitchenInfo,
}) => {
  // Hooks
  const t = useTranslations()
  const openNote = useOpenNote()
  const dispatch = useMyDispatch()
  const { getProductPrice } = useGetProductPrice()
  const { getProductName } = useGetTranslatedNames()
  const addOrRemoveFromWishlist = WishlistSlice.actions.addOrRemoveFromWishlist

  // Store
  const { wishlist } = useMySelector((state) => state)
  const { paymentVariant } = useMySelector((state) => state.app.menu)
  const { notesAvailable } = useMySelector((state) => state.app.menu)
  const { Currency } = useMySelector((state) => state.app.companyData)
  const { colour } = useMySelector((state) => state.app.interfaceStyles)
  // State
  const [isImgErrored, setIsImageErrored] = useState(false)
  const [productsNotes, setProductsNotes] = useState<{
    [key: string]: string
  }>({})

  // useEffects
  useEffect(() => {
    setProductsNotes(handleLocalStorage('products_notes'))
  }, [])

  // Functions
  const handleNoteClick = (e: any) => {
    if (notesAvailable === 1) {
      e.stopPropagation()
      openNote(
        product?.id,
        product?.categoryId,
        setProductsNotes,
        !(location.pathname === '/payment' && paymentVariant === 2)
      )
    }
  }

  const handleWishListClick = (e: any) => {
    e.stopPropagation()
    dispatch(
      addOrRemoveFromWishlist({
        id: product?.id,
        productTemplateId: templateId,
      })
    )
    // handleWishlist({ id: product?.id, productTemplateId: templateId })
  }

  const handleProductClick = (
    product: IProduct,
    e: MouseEvent<HTMLElement>
  ) => {
    if (onProductClick && Boolean(!product?.soldOut)) {
      onProductClick(product, e)
    }
  }

  const isInWishlist = (idToCheck: number) => {
    return !!wishlist?.find(
      (listItem) =>
        listItem.id === idToCheck && listItem.productTemplateId === templateId
    )
  }

  const prices = getProductPrice(product, templateId)
  const leadDays = getItemLeadDays((product as any)?.ClickCollectLeadDaysCount)
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays,
    pickUpTime: handleLocalStorage('pickUpTime'),
  })

  const getPriceToShow = () => {
    if (showPricePerItem) {
      return prices.finalPriceString
    }
    const amount = product?.amount || 1
    return convertToPrice(prices.finalPrice * amount)
  }

  const getIncludedItemPrice = (price?: number) => {
    if (price) {
      return `${convertToPrice(price)} ${Currency || ''}`
    }
    if (!price) {
      return t('order.free')
    }
  }

  return (
    <ProductStyled
      id={`product_${product?.id}`}
      isSelected={isSelected}
      // TODO: add type
      onClick={(e: any) => handleProductClick(product, e)}
    >
      <ImageContainer
        count={(product?.amount as number) || amount}
        isSmall={isImgErrored}
        isFlatware={Boolean(product.innerCategoryItems?.length)}
      >
        <RenderIf condition={Boolean(!product.innerCategoryItems?.length)}>
          <RenderIf condition={!isImgErrored}>
            <StyledImage
              src={product?.imageUrl || 'ERRORED_IMAGE'}
              onError={({ currentTarget }: any) => {
                setIsImageErrored(true)
                currentTarget.onerror = null // prevents looping
              }}
              alt={getProductName(product)}
              // alt={product?.title}
              width="75"
              height="75"
            />
          </RenderIf>
          <RenderIf condition={isImgErrored}>
            <ImagePlaceholder>
              <CameraIcon />
            </ImagePlaceholder>
          </RenderIf>
        </RenderIf>
        <RenderIf condition={Boolean(product.innerCategoryItems?.length)}>
          <RenderIf condition={!isImgErrored}>
            <StyledImage
              src={product?.imageUrl || 'ERRORED_IMAGE'}
              onError={({ currentTarget }: any) => {
                setIsImageErrored(true)
                currentTarget.onerror = null // prevents looping
              }}
              alt={getProductName(product)}
              width="75"
              height="75"
            />
          </RenderIf>
          <RenderIf condition={isImgErrored}>
            <FlatwareImageContainer>
              <IconFilled color={colour}>
                <Squares />
              </IconFilled>
            </FlatwareImageContainer>
          </RenderIf>
        </RenderIf>
      </ImageContainer>
      <RenderIf condition={Boolean(product.innerCategoryItems?.length)}>
        <SubTemplateContainer>
          <p>{getProductName(product)}</p>
          {/* <p>{product?.title}</p> */}
          <ArrowRight />
        </SubTemplateContainer>
      </RenderIf>
      <RenderIf condition={Boolean(!product.innerCategoryItems?.length)}>
        <Name altCount={altCount}>
          <p>{getProductName(product)}</p>
          {/* <p>{product?.title}</p> */}
        </Name>
        <Note>
          <RenderIf
            condition={
              location.pathname !== '/menu' &&
              !!productsNotes?.[`${product?.categoryId}_${product?.id}`]
            }
          >
            <p>{productsNotes?.[`${product?.categoryId}_${product?.id}`]}</p>
          </RenderIf>
          <RenderIf condition={location.pathname === '/menu'}>
            <AllergensContainer>
              {product?.allergens?.map((allergen) => (
                <Allergen
                  key={allergen.id}
                  alt={String(allergen.id)}
                  src={AllergenImages[allergen.id] || AllergenImages[0]}
                />
              ))}
            </AllergensContainer>
          </RenderIf>
        </Note>
        <RenderIf condition={isProductProtected(product)}>
          <AgeProtection>{getProtectedAge(product)}</AgeProtection>
        </RenderIf>
        <RenderIf
          condition={
            notesAvailable === 1 &&
            location.pathname !== '/menu' &&
            !(location.pathname === '/payment' && paymentVariant === 2)
          }
        >
          <SecondaryImage onClick={handleNoteClick}>
            <IconStroked>
              <NoteIcon />
            </IconStroked>
          </SecondaryImage>
        </RenderIf>
        <RenderIf condition={location.pathname === '/menu'}>
          <SecondaryImage onClick={handleWishListClick}>
            <img
              alt="wishlist"
              src={isInWishlist(product?.id) ? fullHeart : emptyHeart}
              width="17"
              height="15"
            />
          </SecondaryImage>
        </RenderIf>
        <RenderIf condition={Boolean(prices.discountValue)}>
          <PriceOG>
            {prices.priceOG} {prices.currency}
          </PriceOG>
        </RenderIf>
        <RenderIf condition={Boolean(prices.finalPrice) || showPricePerItem}>
          <Price isSoldOut={Boolean(product?.soldOut) || isLeadDaysExceeded}>
            <span>{getPriceToShow()}</span>
            &thinsp;
            <span>{Currency || ''}</span>
            <RenderIf condition={Boolean(product?.soldOut)}>
              <SoldOutBanner isList>{t('products.soldOut')}</SoldOutBanner>
            </RenderIf>
            <RenderIf condition={!product?.soldOut && isLeadDaysExceeded}>
              <SoldOutBanner isList>
                {formatLeadDaysNotice(t, leadDays)}
              </SoldOutBanner>
            </RenderIf>
          </Price>
        </RenderIf>
        <RenderIf
          condition={!!includedItems?.length || !!selectedKitchenInfo?.length}
        >
          <IncludedItems>
            <RenderIf condition={!!selectedKitchenInfo?.length}>
              <KitchenItemContainer>
                {selectedKitchenInfo?.map((el) => (
                  <KitchenItem key={`kitchenInfo_${el}`}>{el}</KitchenItem>
                ))}
              </KitchenItemContainer>
            </RenderIf>

            {includedItems?.map((currentItem) => {
              // TODO: check. using templateId is possibly wrong here.
              const itemPrice = getProductPrice(currentItem, templateId)
              return (
                <p key={`includedItem_${currentItem.productId}`}>{`${32} ${
                  currentItem.name
                } (${getIncludedItemPrice(itemPrice.finalPrice)})`}</p>
              )
            })}
          </IncludedItems>
        </RenderIf>
      </RenderIf>
    </ProductStyled>
  )
}

// interface ProductPropsExtended extends ProductProps {
//   withLazy?: boolean
// }

// const ProductListedHOC =
//   (Component: FC<ProductProps>): FC<ProductPropsExtended> =>
//   ({ withLazy = false, product, ...rest }) => {
//     return (
//       <>
//         <RenderIf condition={withLazy}>
//           <LazyLoad height={87} once key={product?.id}>
//             <Component product={product} {...rest} />
//           </LazyLoad>
//         </RenderIf>
//         <RenderIf condition={!withLazy}>
//           <Component product={product} {...rest} />
//         </RenderIf>
//       </>
//     )
//   }

export const ProductListed = ProductListedComponent
